import { ClickAwayListener } from '@mui/material';
import { Input } from 'components/Form/Input/Input';
import {
    AddressSuggestion,
    usePlacesAutocomplete
} from 'components/OnlineBooking/Walking/Address/usePlacesAutocomplete';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import {
    isSelectedPlaceComplete,
    SelectedPlace
} from 'actions/onlineBooking/actions/OnlineBookingWalkingAddress';
import classes from './AddressAutocomplete.module.scss';

const toRegionCode = (value?: string): string | undefined => {
    const code = value?.trim().toLowerCase();

    return code && /^[a-z]{2}$/.test(code) ? code : undefined;
};

interface AddressAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onSelect: (place: SelectedPlace) => void;
    onSelectFailed: () => void;
    onPlacesUnavailable?: () => void;
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = props => {
    const marketplace = useMarketplace();
    const onPlacesUnavailable = props.onPlacesUnavailable;
    const { suggestions, ready, selectSuggestion, clearSkip, placesUnavailable } =
        usePlacesAutocomplete(props.value, toRegionCode(marketplace.address?.country));
    const [open, setOpen] = React.useState(false);

    React.useEffect(() => {
        if (placesUnavailable) {
            onPlacesUnavailable?.();
        }
    }, [placesUnavailable, onPlacesUnavailable]);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        clearSkip();
        props.onChange(event.target.value);
        setOpen(true);
    };

    const selectHandler = async (suggestion: AddressSuggestion) => {
        setOpen(false);

        const place = await selectSuggestion(suggestion).catch(() => undefined);

        if (isSelectedPlaceComplete(place)) {
            props.onSelect(place);
            return;
        }

        props.onSelectFailed();
    };

    const showMenu = open && props.value.trim().length > 0 && (ready || suggestions.length > 0);

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <div className={classes.root}>
                <Input
                    className={classes.field}
                    value={props.value}
                    placeholder="Type your address"
                    autoComplete="off"
                    onChange={changeHandler}
                    onFocus={() => setOpen(true)}
                />

                {showMenu && (
                    <div className={classes.menu}>
                        {suggestions.map(suggestion => (
                            <button
                                key={suggestion.id}
                                type="button"
                                className={classes.option}
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => selectHandler(suggestion)}
                            >
                                {suggestion.label}
                            </button>
                        ))}

                        {suggestions.length === 0 && (
                            <p className={classes.empty}>No matching addresses</p>
                        )}
                    </div>
                )}
            </div>
        </ClickAwayListener>
    );
};
